import { SystemGroup, SystemPlan } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { eventPushCategory, PushCategory } from "@ews/zlt-events";
import { IonCard, IonCardContent, IonCol, IonGrid, IonRow, IonToast } from "@ionic/react";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useCurrentSystemId } from "../Authorization";
import Fullscreen from "../Fullscreen/Fullscreen";
import { useNamedRoutes } from "../NamedRoutes";
import { usePersistentState } from "../Persistence";
import { SystemElement, SystemSetup, useDeleteSystemPlan, useModifySystemElement, useModifySystemPlan, useSystemDetails, useSystemPlan, useSystemPlansWithoutImagesV2, useSystemPlansWithoutImages, useSystemSetup } from "../ReactData/system";
import { useIsMobile } from "../Responsive";
import style from "./Plan.module.scss";
import { getMarkerId } from "./components/Element/Element";
import ElementList from "./components/ElementList/ElementList";
import Floors from "./components/Floors/Floors";
import FloorEdit from "./components/Floors/components/FloorEdit/FloorEdit";
import Map from "./components/Map/Map";
import { PlanCoords } from "./components/Map/types";
import { Device, PlanProps } from "./types";
import { floatingPromiseHandler } from "../../utils";
import { hasMainPlan, isMainPlan } from "./planUtils";


// moved to components/Plan/components/ElementList.tsx
// export const fileIconSize = "";

type Marker = [SystemGroup, SystemElement];

function getMarkers(groups: SystemGroup[], systemPlanId: string)
{
    const markers: Marker[] = [];

    const where = (element: SystemElement): Boolean =>
    {
        return element.systemPlanX !== null && element.systemPlanX !== null && element.systemPlanId === systemPlanId;
    };

    groups.filter(group => group.elements?.length).forEach((group) =>
    {
        group.elements?.forEach((element: SystemElement) =>
        {
            if (where(element)) markers.push([group, element]);
        });
    });
    return markers;
}

// moved to planUtils.ts
// export const isMainPlan = (plan: any) => plan ?
//     (plan.isMain !== undefined || plan.isMain !== null) && plan.isMain === true
//     :
//     false;

// export const hasMainPlan = (plans: any[]) =>
//     plans.reduce((val, plan) => val ?
//         true
//         :
//         isMainPlan(plan),
//         false);

function getGroupsWithUnsetElements(groups: SystemGroup[])
{
    const where = (element: SystemElement) => (!("systemPlanX" in element) || element?.systemPlanX === null) && (!("systemPlanY" in element) || element?.systemPlanY === null);

    const copy = [...groups];

    return copy.map(group =>
    {

        const copyGroup = { ...group } as SystemGroup;

        if (copyGroup.elements?.length) {
            copyGroup.elements = copyGroup.elements.filter((element: SystemElement) => where(element));
        };
        return copyGroup;
    });
}

const Aside: React.FC<{ children?: ReactNode, mobileView: boolean, className?: string; }> = ({ children, mobileView, className }) =>
{
    return <>{children}</>;
};

const PushCategoryList = Object.entries(PushCategory).
    filter((([key, val]) => (key !== "None" && Number(key) !== 0) && isNaN(Number(key))))
    .sort((a, b) => Number(a[1]) - Number(b[1]));



const getFloorsWithEvent = (setup: SystemSetup) =>
{

    const getHighestPushCategoryEvent = (event: PushCategory): [string, PushCategory] =>
    {
        for (const category of PushCategoryList) {
            const [key, val] = category;
            const bit = Number(val);

            if ((event & bit) === bit) {
                return [key, val] as [string, PushCategory];
            }
        }
        return ["None", PushCategory.None];
    };

    const events: Record<string, PushCategory> = {};

    for (const group of setup.groups) {

        for (const element of group.elements || []) {

            if (!element.systemPlanId) continue;

            let eventKindType = eventPushCategory(element.eventKindType || 0);
            if (eventKindType !== PushCategory.None) {

                if (`${element.systemPlanId}` in events) {
                    eventKindType = events[element.systemPlanId] | eventKindType;
                }

                events[element.systemPlanId] = getHighestPushCategoryEvent(eventKindType)[1];
            }
        }
    }
    return events;
};

const Plan: React.FC<PlanProps> = ({ onClick }) =>
{
    const { params, router, generate } = useNamedRoutes();
    const { translate: t } = useL10n();

    const id = useCurrentSystemId();
    const { planId } = params();

    const setup = useSystemSetup(id!);




    const floors = useSystemPlansWithoutImagesV2(id || "");
    // const floors = useSystemPlansWithoutImages(id || "");



    // const floors = useSystemPlans(id || "")?.results;

    const mobileView = useIsMobile();

    const modifyElement = useModifySystemElement();
    const [planEdit] = usePersistentState<boolean>("authorizationLevelPlan");

    const [editFloor, setEditFloor] = useState<SystemPlan | undefined>(undefined);
    const [currentElement, setCurrentElement] = useState<SystemElement | undefined>(undefined);
    const [currentGroup, setCurrentGroup] = useState<SystemGroup | undefined>(undefined);
    const [currentIcon, setCurrentIcon] = useState<Device | undefined>(undefined);
    const ErrorToastRef = useRef<HTMLIonToastElement>(null);
    const [currentFloor, setCurrentFloor] = useState<SystemPlan | undefined>(undefined);
    const [selectedPlanButton, setSelectedPlanButton] = useState<SystemPlan | undefined>(undefined);
    const [bounds, setBounds] = useState<[number, number]>([0, 0]);
    const [markers, setMarkers] = useState<([SystemGroup, SystemElement])[]>([]);
    const [unsetElements, setUnsetElements] = useState<SystemGroup[]>([]);
    const _planId = useRef<typeof planId>(undefined);

    // TODO: remove this: it does nothing
    const _floors = useMemo(() => floors, [floors, setup, currentFloor]);
    //const alarmFloors = getFloorsWithEvent(setup);
    const [showFloors, setShowFloors] = useState(true);

    const modifySystemPlan = useModifySystemPlan();
    const deleteSystemPlan = useDeleteSystemPlan();
    const systemDetails = useSystemDetails(id || "");

    const currentFloorWithImage = useSystemPlan(id || "", currentFloor?.id || "");


    async function modifyPlan(systemId: string, planId: string, data: SystemPlan)
    {
        await modifySystemPlan(systemId, data as SystemPlan, planId);
    }

    async function deletePlan(systemId: string, planId: string)
    {
        await deleteSystemPlan(systemId, planId);
    }


    function filterSetup(setup: SystemSetup, id: string)
    {
        const markers = getMarkers(setup.groups, id);
        const unsetMarkers = getGroupsWithUnsetElements(setup.groups);
        setMarkers(markers);
        setUnsetElements(unsetMarkers);
    }

    async function saveMarker(
        coords: PlanCoords,
    )
    {
        if (!currentIcon) {
            if (ErrorToastRef) {
                ErrorToastRef.current!.message = t("[REACT_SELECT_DEVICE_IMAGE]");
                ErrorToastRef.current?.present().catch(floatingPromiseHandler(import.meta.url));
            };
            return;
        };
        if (currentElement) {
            const element = {
                systemPlanIcon: currentIcon?.img || "",
                systemPlanId: currentFloor?.id || "",
                systemPlanX: coords.x,
                systemPlanY: coords.y,
                systemPlanStyle: systemDetails.defaultPlanStyle
            };


            await modifyElement(
                currentElement.systemId!,
                currentElement?.numberGroup!,
                currentElement?.groupId!,
                currentElement?.id!,
                element as any
            );
            setCurrentElement(undefined);
        }
    }

    async function saveLabel(coords: PlanCoords)
    {
        if (!selectedPlanButton || (!selectedPlanButton.id && !selectedPlanButton.systemId)) return;

        await modifyPlan(selectedPlanButton.systemId!, selectedPlanButton.id!, {
            ...selectedPlanButton,
            labelPositionX: coords.x,
            labelPositionY: coords.y,
        } as SystemPlan);

        setSelectedPlanButton(undefined);

    }

    async function deleteMarker(element: SystemElement)
    {
        await modifyElement(
            element.systemId!,
            element?.numberGroup!,
            element?.groupId!,
            element?.id!,
            {
                ...element,
                systemPlanId: null,
                systemPlanX: null,
                systemPlanY: null,
                systemPlanIcon: null,
                systemPlanStyle: {}
            } as any,
        );
    }


    async function modifyMarker(element: SystemElement, modified: { [key: string]: any; })
    {
        await modifyElement(
            element.systemId!,
            element.numberGroup!,
            element.groupId!,
            element.id!,
            { ...modified } as any,
        );
    }


    if (floors !== _floors) {
        console.log("====== floors !== _floors ??????????????????", floors === _floors);
    }

    if (floors.length === 0) {
        console.log("====== floors.length === 0 !!!!!!!!!!!!!", floors, _floors);
    }

    useEffect(() =>
    {
        if (floors && floors.length && setup.groups.length) {
            if ((planId || _planId.current) && floors.findIndex((floor) => floor.id === (planId || _planId.current)) > -1) {
                const floor = floors[floors.findIndex((floor) => floor.id === (planId || _planId.current))];
                setCurrentFloor(floor);
                filterSetup(setup, floor.id!);
            }
            else if (!currentFloor) {

                let planId = null;
                if (hasMainPlan(floors)) {
                    const mainPlan = floors.find(floors => floors.isMain === true);
                    if (mainPlan) planId = mainPlan.id;
                    else planId = floors[0].id;
                } else {
                    planId = floors[0].id;
                }
                const firstFloorRoute = generate("system:plan", { id, planId });
                router?.push(firstFloorRoute);
            }
        }

        return () =>
        {
            markers.forEach(([_, marker]) => document.querySelector("#" + getMarkerId(marker))?.remove());
        };
    },
        [_floors, setup, currentFloor, planId]
    );

    const events = getFloorsWithEvent(setup);

    return <>
        <Fullscreen>
            <IonCard className={`${style.plan}`}>
                <IonCardContent className={`${style.planCardContent}`}>
                    {mobileView && <Aside
                        mobileView={mobileView}
                        className={`${style.aside} ${planEdit ? style.full : ""}`}
                    >
                        <Floors
                            systemId={id}
                            current={currentFloor}
                            floors={_floors}
                            //alarmFloors={alarmFloors}
                            events={events}
                            onChange={(floor) =>
                            {
                                setCurrentFloor(floor);
                                setBounds(bounds);
                            }}
                            onContext={(floor) =>
                            {
                                setEditFloor(floor);
                            }}
                        />
                    </Aside>}
                    {currentFloor && <div>
                        <h1>{currentFloor.label}</h1>
                    </div>}
                    <div className={`${style.contentContainer}`}>

                        <IonGrid className={style.planGrid}>
                            <IonRow className={style.planRow}>
                                <IonCol className={style.planCol}>

                                    <div
                                        className={`${style.planMapContainer} ${planEdit ? "" : mobileView ? style.fullWidthMobile : style.fullWidth}`}
                                    >
                                        {currentFloor ?
                                            <Map
                                                zoom={mobileView ? -3 : -2}
                                                isMainPlan={isMainPlan(currentFloor)}
                                                events={events}
                                                onDeleteElement={deleteMarker}
                                                onEditElement={modifyMarker}
                                                onDeletePlanButton={(plan) =>
                                                {
                                                    const data = {
                                                        labelPositionX: null,
                                                        labelPositionY: null,
                                                    };

                                                    if (plan.systemId && plan.id) {
                                                        modifyPlan(plan.systemId, plan.id, data as any).catch(floatingPromiseHandler(import.meta.url));
                                                    }
                                                }}
                                                onEditPlanButton={(plan, data) =>
                                                {
                                                    if (plan.systemId && plan.id) {
                                                        modifyPlan(plan.systemId, plan.id, data as SystemPlan).catch(floatingPromiseHandler(import.meta.url));
                                                    }
                                                }}
                                                onSetMarkerPosition={saveMarker}
                                                onSetLabelPosition={saveLabel}
                                                onAlignment={(elements) =>
                                                {
                                                    elements.forEach(element =>
                                                    {
                                                        modifyMarker(element, element).catch(floatingPromiseHandler(import.meta.url));
                                                    });
                                                }}
                                                url={currentFloorWithImage.image as string}
                                                //elementList={[]}
                                                elementList={markers}
                                                planList={floors}
                                            />

                                            : null
                                        }
                                    </div>

                                </IonCol>
                                {!mobileView &&
                                    <IonCol className={style.planCol}>
                                        <Aside
                                            mobileView={mobileView}
                                            className={`${style.aside} ${planEdit ? style.full : ""}`}
                                        >

                                            <IonGrid className={`${style.asideGrid} ${!showFloors && planEdit ? style.hidePlanListContent : ""} ${!planEdit ? style.onlyFirstChild : ""}`}>{/* ${planEdit ? style.full : ""} */}
                                                <IonRow className={` ${style.asideRow} `}>

                                                    <IonCol className={`${style.asideCol} `}>
                                                        <Floors
                                                            systemId={id}
                                                            current={currentFloor}
                                                            floors={_floors}
                                                            events={events}
                                                            showFloors={showFloors}
                                                            //alarmFloors={alarmFloors}
                                                            onChange={(floor) =>
                                                            {
                                                                setCurrentFloor(floor);
                                                                setBounds(bounds);
                                                            }}
                                                            onContext={(floor) =>
                                                            {
                                                                setEditFloor(floor);
                                                            }}
                                                            onShowFloors={(state) => setShowFloors(state)}
                                                        />
                                                    </IonCol>

                                                </IonRow>
                                                <IonRow className={` ${style.asideRow}`}>
                                                    {!(planEdit) ?
                                                        null
                                                        :

                                                        <IonCol className={`${style.asideCol} `}>
                                                            <ElementList
                                                                isMainPlan={isMainPlan(currentFloor)}
                                                                plans={floors}
                                                                showFloors={showFloors}
                                                                currentPlan={selectedPlanButton}
                                                                currentGroup={currentGroup}
                                                                currentElement={currentElement}
                                                                groups={unsetElements}
                                                                iconData={currentIcon}
                                                                onElement={(element) => setCurrentElement(element)}
                                                                onGroup={(group) => setCurrentGroup(group)}
                                                                onChange={(icon) => setCurrentIcon(icon)}
                                                                onFloor={floor => setSelectedPlanButton(floor)}
                                                            />
                                                        </IonCol>
                                                    }

                                                </IonRow>
                                            </IonGrid>;
                                        </Aside>
                                    </IonCol>}
                            </IonRow>
                        </IonGrid>
                    </div>
                    {mobileView ?
                        null
                        :
                        <FloorEdit
                            systemId={id || ""}
                            floors={floors}
                            floor={editFloor}
                            onDelete={(plan) =>
                            {
                                if (id) {
                                    if (isMainPlan(plan)) {
                                        floors.forEach(floor =>
                                        {
                                            const labelPositionX = undefined;
                                            const labelPositionY = undefined;

                                            if (floor.systemId && floor.id) {
                                                modifyPlan(floor.systemId, floor.id, { labelPositionX, labelPositionY } as SystemPlan).catch(floatingPromiseHandler(import.meta.url));
                                            }
                                        });

                                    } else {
                                        markers.forEach(([_, marker]) =>
                                        {
                                            if (marker.systemPlanId === plan.id) {
                                                deleteMarker(marker).catch(floatingPromiseHandler(import.meta.url));
                                            }
                                        });
                                    }
                                    deletePlan(id, plan.id!).catch(floatingPromiseHandler(import.meta.url));

                                    if (currentFloor?.id === planId) setCurrentFloor(undefined);
                                }
                            }
                            }
                            onModified={(modifiedFloor) =>
                            {
                                if (id && modifiedFloor.id) {
                                    modifyPlan(id, modifiedFloor.id, modifiedFloor).catch(floatingPromiseHandler(import.meta.url));
                                }
                            }}
                            onDismiss={() => { setEditFloor(undefined); }}
                        />
                    }
                </IonCardContent>
            </IonCard >
            <IonToast
                ref={ErrorToastRef}
                className={style["error-msg"]}
                position="middle"
                color={"danger"}
                duration={300}
            />
        </Fullscreen>
    </>;
};

export default Plan;
